// /admin → Observability. The platform's health, read from CloudWatch IN THE
// BROWSER with the operator's own scoped credentials (the `observe` capability).
//
// No metrics backend, no agent, no third-party analytics script holding our
// users' traffic. The same argument the platform makes about databases — AWS is
// already doing this; don't rebuild it — applied to telemetry.

import {
	Alert,
	Anchor,
	Badge,
	Card,
	Group,
	Loader,
	SimpleGrid,
	Stack,
	Table,
	Text,
	Title,
} from "@mantine/core";
import type { AlarmState, FunctionSeries } from "@s3p/sdk";
import { IconAlertTriangle, IconCircleCheck, IconHelpCircle } from "@tabler/icons-react";
import { useEffect, useState } from "react";
// The root project's slug, at RUNTIME (a value, not a type) — from the SDK served
// at /sdk/index.js, the same path project-config.ts imports it from. Without this
// the module threw `ROOT_SLUG is not defined` at load, and because App.tsx imports
// this component eagerly, that crash white-screened the WHOLE app, not just /admin.
import { ROOT_SLUG } from "/sdk/index.js";
import { useSession } from "../../session/SessionProvider.tsx";

/** One project's reported SDK usage (written by `s3p deploy`). */
interface UsageManifest {
	slug: string;
	features: Record<string, number>;
	serverFunctions: string[];
	sdkVersion?: string;
	cliVersion?: string;
	reportedAt?: string;
}

const REGION = "eu-central-1";
const DASHBOARD_URL = `https://${REGION}.console.aws.amazon.com/cloudwatch/home?region=${REGION}#dashboards/dashboard/s3p-${ROOT_SLUG}`;

// The root project's function names are `s3p-{ROOT_SLUG}-{...}`. Hardcoding the
// old `s3p-platform-*` prefix pointed this panel at Lambdas that no longer exist
// after the platform→console rename — so it read metrics for dead functions and
// showed nothing. Derived from ROOT_SLUG so it tracks the root project's actual
// name, whatever it is.
const FN = (suffix: string) => `s3p-${ROOT_SLUG}-${suffix}`;

/** Fallback when the deployed manifest can't be read — the functions whose
 *  failure stops signups and provisioning. The live list comes from
 *  `client.functions.deployed()` so a newly-deployed handler shows up here
 *  without editing this file. */
const FALLBACK_FUNCTIONS = [
	FN("runtime"),
	FN("fn-provision-project"),
	FN("fn-manage-operators"),
	FN("fn-delete-project"),
	FN("fn-send-email"),
	FN("fn-fulfill-platform-request"),
	FN("fn-submit-feedback"),
	FN("fn-on-signup"),
];

function StateIcon({ state }: { state: string }) {
	if (state === "ALARM") return <IconAlertTriangle size={16} color="var(--mantine-color-red-5)" />;
	if (state === "OK") return <IconCircleCheck size={16} color="var(--mantine-color-teal-5)" />;
	return <IconHelpCircle size={16} color="var(--mantine-color-gray-6)" />;
}

export function Observability() {
	const { client } = useSession();
	const [fns, setFns] = useState<FunctionSeries[] | null>(null);
	const [alarms, setAlarms] = useState<AlarmState[] | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!client) return;
		let cancelled = false;
		void (async () => {
			try {
				// The platform's own inventory, from the deployed-functions manifest —
				// plus the shared runtime Lambda, which is not a fn-* function.
				const deployed = await client.functions.deployed().catch(() => []);
				const fnNames = deployed.length
					? [FN("runtime"), ...deployed.map((d) => FN(`fn-${d.name}`))]
					: FALLBACK_FUNCTIONS;
				const [f, a] = await Promise.all([
					client.metrics.functions(fnNames, 24),
					client.metrics.alarms(),
				]);
				if (cancelled) return;
				setFns(f);
				setAlarms(a);
			} catch (e) {
				if (!cancelled) setError(e instanceof Error ? e.message : String(e));
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [client]);

	if (error) {
		return (
			<Alert color="red" variant="light" title="Couldn't read telemetry">
				{error}
				<Text size="sm" mt="xs" c="dimmed">
					This panel needs the <b>observe</b> capability. If the role predates it,
					re-sync roles from the project's console → Roles.
				</Text>
			</Alert>
		);
	}

	const firing = (alarms ?? []).filter((a) => a.state === "ALARM");
	const totals = (fns ?? []).reduce(
		(acc, f) => ({
			invocations: acc.invocations + f.invocations,
			errors: acc.errors + f.errors,
		}),
		{ invocations: 0, errors: 0 },
	);

	return (
		<Stack gap="lg">
			<Group justify="space-between" align="flex-end">
				<Stack gap={2}>
					<Title order={2}>Observability</Title>
					<Text c="dimmed" size="sm">
						Last 24 hours, read live from CloudWatch with your own credentials.
					</Text>
				</Stack>
				<Anchor href={DASHBOARD_URL} target="_blank" size="sm">
					Full dashboard in AWS ↗
				</Anchor>
			</Group>

			{firing.length > 0 && (
				<Alert color="red" variant="light" title={`${firing.length} alarm(s) firing`}>
					<Stack gap={4}>
						{firing.map((a) => (
							<Text key={a.name} size="sm">
								<b>{a.name}</b> — {a.description ?? a.reason}
							</Text>
						))}
					</Stack>
				</Alert>
			)}

			<SimpleGrid cols={{ base: 1, sm: 3 }}>
				<Card withBorder radius="md" padding="md">
					<Text size="xs" c="dimmed" tt="uppercase" fw={700}>
						Invocations (24h)
					</Text>
					<Text fz={28} fw={700}>
						{fns ? totals.invocations.toLocaleString() : <Loader size="xs" />}
					</Text>
				</Card>
				<Card withBorder radius="md" padding="md">
					<Text size="xs" c="dimmed" tt="uppercase" fw={700}>
						Errors (24h)
					</Text>
					<Text fz={28} fw={700} c={totals.errors > 0 ? "red" : undefined}>
						{fns ? totals.errors.toLocaleString() : <Loader size="xs" />}
					</Text>
				</Card>
				<Card withBorder radius="md" padding="md">
					<Text size="xs" c="dimmed" tt="uppercase" fw={700}>
						Alarms
					</Text>
					<Text fz={28} fw={700} c={firing.length > 0 ? "red" : "teal"}>
						{alarms ? (firing.length > 0 ? `${firing.length} firing` : "all clear") : <Loader size="xs" />}
					</Text>
				</Card>
			</SimpleGrid>

			<Card withBorder radius="md" padding="md">
				<Title order={4} mb="sm">
					Platform functions
				</Title>
				{!fns ? (
					<Group justify="center" py="md">
						<Loader size="sm" />
					</Group>
				) : (
					<Table striped highlightOnHover>
						<Table.Thead>
							<Table.Tr>
								<Table.Th>Function</Table.Th>
								<Table.Th ta="right">Invocations</Table.Th>
								<Table.Th ta="right">Errors</Table.Th>
								<Table.Th ta="right">Throttles</Table.Th>
								<Table.Th ta="right">Avg duration</Table.Th>
							</Table.Tr>
						</Table.Thead>
						<Table.Tbody>
							{fns.map((f) => (
								<Table.Tr key={f.function}>
									<Table.Td>
										<Text size="sm" ff="monospace">
											{f.function.replace(`s3p-${ROOT_SLUG}-`, "")}
										</Text>
									</Table.Td>
									<Table.Td ta="right">{f.invocations.toLocaleString()}</Table.Td>
									<Table.Td ta="right">
										{f.errors > 0 ? (
											<Badge color="red" variant="light">
												{f.errors}
											</Badge>
										) : (
											"0"
										)}
									</Table.Td>
									<Table.Td ta="right">{f.throttles}</Table.Td>
									<Table.Td ta="right">{f.avgDurationMs ? `${f.avgDurationMs} ms` : "—"}</Table.Td>
								</Table.Tr>
							))}
						</Table.Tbody>
					</Table>
				)}
			</Card>

			<FeatureUsage />

			<Card withBorder radius="md" padding="md">
				<Title order={4} mb="sm">
					Alarms
				</Title>
				{!alarms ? (
					<Group justify="center" py="md">
						<Loader size="sm" />
					</Group>
				) : (
					<Stack gap={6}>
						{alarms.map((a) => (
							<Group key={a.name} gap="xs" wrap="nowrap">
								<StateIcon state={a.state} />
								<Text size="sm" ff="monospace" style={{ minWidth: 260 }}>
									{a.name}
								</Text>
								<Text size="sm" c="dimmed" lineClamp={1}>
									{a.state === "INSUFFICIENT_DATA" ? "no data yet (nothing has happened)" : a.description}
								</Text>
							</Group>
						))}
					</Stack>
				)}
			</Card>
		</Stack>
	);
}


/** WHO BREAKS IF I CHANGE THIS? — the inventory every safe upgrade needs.
 *
 *  Each `s3p deploy` reports which SDK APIs the app actually calls. Before
 *  narrowing a grant, deprecating a method or swapping the data layer, read
 *  this: it's the difference between "nobody uses transaction()" and hoping. */
function FeatureUsage() {
	const { client } = useSession();
	const [rows, setRows] = useState<UsageManifest[] | null>(null);

	useEffect(() => {
		if (!client) return;
		let cancelled = false;
		void (async () => {
			try {
				const all = await client
					.collection<UsageManifest>("usage", { filename: "usage.json" })
					.list();
				if (!cancelled) setRows(all);
			} catch {
				if (!cancelled) setRows([]);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [client]);

	if (!rows) {
		return (
			<Card withBorder radius="md" padding="md">
				<Group justify="center" py="md">
					<Loader size="sm" />
				</Group>
			</Card>
		);
	}

	// Feature → the projects using it. This is the column you read before you
	// break something.
	const byFeature = new Map<string, string[]>();
	for (const r of rows) {
		for (const f of Object.keys(r.features ?? {})) {
			byFeature.set(f, [...(byFeature.get(f) ?? []), r.slug]);
		}
	}
	const features = [...byFeature.entries()].sort((a, b) => b[1].length - a[1].length);

	return (
		<Card withBorder radius="md" padding="md">
			<Group justify="space-between" mb={4}>
				<Title order={4}>SDK feature usage</Title>
				<Text size="xs" c="dimmed">
					{rows.length} project(s) reporting
				</Text>
			</Group>
			<Text size="sm" c="dimmed" mb="sm">
				Reported by <code>s3p deploy</code>. Read this <b>before</b> changing an API:
				it tells you exactly who breaks — the inventory the upgrade doctrine's
				"contract" phase requires.
			</Text>
			{rows.length === 0 ? (
				<Text size="sm" c="dimmed">
					No reports yet. Projects report on their next deploy.
				</Text>
			) : (
				<Table striped highlightOnHover>
					<Table.Thead>
						<Table.Tr>
							<Table.Th>Feature</Table.Th>
							<Table.Th ta="right">Projects</Table.Th>
							<Table.Th>Who</Table.Th>
						</Table.Tr>
					</Table.Thead>
					<Table.Tbody>
						{features.map(([f, slugs]) => (
							<Table.Tr key={f}>
								<Table.Td>
									<Text size="sm" ff="monospace">
										{f}
									</Text>
								</Table.Td>
								<Table.Td ta="right">
									<Badge variant="light" color={slugs.length > 0 ? "blue" : "gray"}>
										{slugs.length}
									</Badge>
								</Table.Td>
								<Table.Td>
									<Text size="sm" c="dimmed" lineClamp={1}>
										{slugs.join(", ")}
									</Text>
								</Table.Td>
							</Table.Tr>
						))}
					</Table.Tbody>
				</Table>
			)}
		</Card>
	);
}
